import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Download, MapPin, Mail, MessageSquare, Tag, Globe } from "lucide-react";
import { API } from "@/lib/book";
import AdminLayout from "@/pages/admin/AdminLayout";

const apiGet = (path) => axios.get(`${API}${path}`, { withCredentials: true }).then((r) => r.data);

function PageHeader({ eyebrow, title, action }) {
  return (
    <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-3">{eyebrow}</div>
        <h1 className="font-display font-black tracking-tighter text-5xl leading-[0.95]">{title}</h1>
      </div>
      {action}
    </div>
  );
}

function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) return;
  const cols = Object.keys(rows[0]);
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const Th = ({ children }) => <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-5 py-4 ${className}`}>{children}</td>;

// ============== ORDERS ==============
export function AdminOrders() {
  const [rows, setRows] = useState(null);
  useEffect(() => { apiGet("/admin/orders?limit=1000").then((d) => setRows(d.orders || [])).catch(() => setRows([])); }, []);
  return (
    <AdminLayout>
      <PageHeader
        eyebrow="All orders"
        title="Orders."
        action={
          <button
            onClick={() => downloadCsv("orders.csv", (rows || []).map((o) => ({
              id: o.id, date: o.created_at, customer_name: o.customer_name, customer_email: o.customer_email,
              quantity: o.quantity, book_total: o.book_total, shipping_cost: o.shipping_cost, total: o.amount_total,
              shipping_service: o.shipping_service,
              street: o.shipping_address?.street1, city: o.shipping_address?.city, state: o.shipping_address?.state,
              zip: o.shipping_address?.zip, country: o.shipping_address?.country,
              session_id: o.session_id,
            })))}
            disabled={!rows || rows.length === 0}
            className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-3 text-sm hover:bg-[#D92D20] disabled:opacity-60"
            data-testid="orders-export-csv"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        }
      />
      {rows === null ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        <div className="bg-white border border-black/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5]">
              <tr><Th>Date</Th><Th>Customer</Th><Th>Qty</Th><Th>Total</Th><Th>Ship to</Th><Th>Stripe</Th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={6} className="px-5 py-10 text-center text-[#525252]">No orders yet.</td></tr> :
                rows.map((o) => (
                  <tr key={o.id} className="border-t border-black/10" data-testid={`admin-order-row-${o.id}`}>
                    <Td>{(o.created_at || "").slice(0, 10)}</Td>
                    <Td>
                      <div className="font-medium">{o.customer_name}</div>
                      <div className="text-xs text-[#525252]">{o.customer_email}</div>
                    </Td>
                    <Td>{o.quantity}</Td>
                    <Td className="font-mono-plex">${o.amount_total?.toFixed(2)}</Td>
                    <Td className="text-xs">
                      <div className="flex items-start gap-1.5"><MapPin className="w-3 h-3 mt-0.5 text-[#D92D20]" />
                        <span>{o.shipping_address?.street1}<br />{o.shipping_address?.city}, {o.shipping_address?.state} {o.shipping_address?.zip} · {o.shipping_address?.country}</span>
                      </div>
                    </Td>
                    <Td className="font-mono-plex text-[10px] break-all">{o.session_id}</Td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

// ============== LEADS ==============
export function AdminLeads() {
  const [rows, setRows] = useState(null);
  useEffect(() => { apiGet("/admin/leads?limit=5000").then((d) => setRows(d.leads || [])).catch(() => setRows([])); }, []);
  return (
    <AdminLayout>
      <PageHeader
        eyebrow="Newsletter list"
        title="Email list."
        action={
          <button
            onClick={() => downloadCsv("email-list.csv", (rows || []).map((r) => ({ email: r.email, source: r.source, date: r.created_at })))}
            disabled={!rows || rows.length === 0}
            className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-3 text-sm hover:bg-[#D92D20] disabled:opacity-60"
            data-testid="leads-export-csv"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        }
      />
      {rows === null ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        <div className="bg-white border border-black/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5]">
              <tr><Th>Date</Th><Th>Email</Th><Th>Source</Th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={3} className="px-5 py-10 text-center text-[#525252]">No signups yet.</td></tr> :
                rows.map((r) => (
                  <tr key={r.id} className="border-t border-black/10">
                    <Td>{(r.created_at || "").slice(0, 10)}</Td>
                    <Td className="font-mono-plex">{r.email}</Td>
                    <Td className="text-xs text-[#525252]">{r.source}</Td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

// ============== CONTACT MESSAGES ==============
export function AdminMessages() {
  const [rows, setRows] = useState(null);
  useEffect(() => { apiGet("/admin/contact?limit=2000").then((d) => setRows(d.messages || [])).catch(() => setRows([])); }, []);
  return (
    <AdminLayout>
      <PageHeader eyebrow="Support inbox" title="Messages." />
      {rows === null ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        rows.length === 0 ? (
          <div className="bg-white border border-black/10 p-10 text-center text-[#525252]">
            <MessageSquare className="w-8 h-8 mx-auto text-[#D92D20] mb-3" />
            No messages yet.
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((m) => (
              <article key={m.id} className="bg-white border border-black/10 p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-display font-bold text-lg">{m.name} <span className="text-[#525252] text-sm font-normal font-mono-plex">· {m.email}</span></div>
                    {m.subject && <div className="text-sm text-[#525252] mt-1">{m.subject}</div>}
                  </div>
                  <div className="text-xs font-mono-plex text-[#737373]">{(m.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <p className="mt-4 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "Your message")}`} className="mt-4 inline-flex items-center gap-2 text-sm text-[#D92D20] underline">
                  <Mail className="w-3 h-3" /> Reply via email
                </a>
              </article>
            ))}
          </div>
        )
      )}
    </AdminLayout>
  );
}

// ============== PROMOS ==============
export function AdminPromos() {
  const [d, setD] = useState(null);
  useEffect(() => { apiGet("/admin/promo-stats").then(setD).catch(() => setD({ codes: [] })); }, []);
  return (
    <AdminLayout>
      <PageHeader eyebrow="Coupons" title="Promo codes." />
      {!d ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
            <div className="bg-white border border-black/10 p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold mb-3">Orders w/ promo</div>
              <div className="font-display font-black text-4xl">{d.total_promo_orders}</div>
            </div>
            <div className="bg-white border border-black/10 p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold mb-3">Discount given · total</div>
              <div className="font-display font-black text-4xl text-[#D92D20]">−${d.total_discount_given.toFixed(2)}</div>
            </div>
          </div>
          <div className="bg-white border border-black/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5]">
                <tr><Th>Code</Th><Th>Uses</Th><Th>Revenue</Th><Th>Discount given</Th></tr>
              </thead>
              <tbody>
                {d.codes.length === 0 ? <tr><td colSpan={4} className="px-5 py-10 text-center text-[#525252]">No promo codes used yet.</td></tr> :
                  d.codes.map((c) => (
                    <tr key={c.code} className="border-t border-black/10">
                      <Td className="font-mono-plex font-medium uppercase">{c.code}</Td>
                      <Td>{c.uses}</Td>
                      <Td className="font-mono-plex">${c.revenue.toFixed(2)}</Td>
                      <Td className="font-mono-plex text-[#D92D20]">−${c.discount_given.toFixed(2)}</Td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

// ============== GEO ==============
export function AdminGeo() {
  const [d, setD] = useState(null);
  useEffect(() => { apiGet("/admin/geo-stats").then(setD).catch(() => setD({ countries: [], us_states: [] })); }, []);
  return (
    <AdminLayout>
      <PageHeader eyebrow="Where orders ship" title="Geography." />
      {!d ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Block title="By country" Icon={Globe} rows={d.countries} keyField="country" />
          <Block title="By US state" Icon={Globe} rows={d.us_states} keyField="state" />
        </div>
      )}
    </AdminLayout>
  );
}

function Block({ title, Icon, rows, keyField }) {
  const max = Math.max(1, ...rows.map((r) => r.orders));
  return (
    <div className="bg-white border border-black/10 p-6">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold mb-5">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#D92D20]" />} {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-[#525252]">No data yet.</div>
      ) : (
        <ul className="space-y-3">
          {rows.slice(0, 25).map((r) => (
            <li key={r[keyField]} className="flex items-center gap-4 text-sm">
              <div className="font-mono-plex w-12 shrink-0">{r[keyField]}</div>
              <div className="flex-1 bg-[#F5F5F5] h-3 relative">
                <div className="absolute inset-y-0 left-0 bg-[#D92D20]" style={{ width: `${(r.orders / max) * 100}%` }} />
              </div>
              <div className="font-mono-plex w-10 text-right">{r.orders}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
